import { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { Outlet } from "react-router-dom";
import { LOCAL_STORAGE } from "shared/constants";
import { Business, User } from "shared/types";
import { ConfirmationResult, RecaptchaVerifier, signInWithPhoneNumber, signOut as firebaseSignOut } from "firebase/auth";
import { auth } from "../firebase";
import { message } from "antd";
import { translate } from "shared/languages";
import { useQueryClient } from "@tanstack/react-query";
import { clearRememberedTablePages } from "hooks/useRememberedTablePage";
import { useCloseStaticModalsOnNavigation } from "hooks/useCloseStaticModalsOnNavigation";
declare global {
  interface Window {
    recaptchaVerifier: import("firebase/auth").RecaptchaVerifier;
  }
}

type AuthState = {
  isLoading: boolean;
  isSignout: boolean;
  jwtToken: string | null;
  refreshToken: string | null;
  user: User | null;
  business: Business | null;
};

type AuthContextType = {
  signIn: (token: any, refreshToken: any, user: User, business: Business | null) => void;
  signOut: () => void;
  confirmationResult?: ConfirmationResult;
  recaptchaVerifier: (phone_number: string) => Promise<void>;
  confirmOtp: (otp: string) => Promise<void>;
  // ID token from the phone sign-in; the server verifies it. Null if none.
  getPhoneProof: () => Promise<string | null>;
  clearPhoneProof: () => Promise<void>;
  state: AuthState;
};

const initialState: AuthState = {
  isLoading: true,
  isSignout: false,
  jwtToken: null,
  refreshToken: null,
  user: null,
  business: null,
};

export const AuthContext = createContext<AuthContextType>({
  signIn: (jwtToken: any, refreshToken: any, user: User, business: Business) => {},
  signOut: () => {},
  state: initialState,
} as AuthContextType);

export const AuthProvider = ({ children }: any) => {
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult>();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer((prevState: any, action: any) => {
    switch (action.type) {
      case "STORE_TOKEN":
        return {
          user: action.user,
          business: action.business,
          jwtToken: action.jwtToken,
          refreshToken: action.refreshToken,
          isLoading: false,
        };
      case "DELETE_TOKEN":
        return {
          ...prevState,
          jwtToken: null,
          user: null,
          business: null,
          refreshToken: null,
          isLoading: true,
        };
    }
  }, initialState);

  // Load tokens and user data into local storage on app startup
  useEffect(() => {
    let user, business, jwtToken, refreshToken;
    jwtToken = localStorage.getItem(LOCAL_STORAGE.TOKEN);
    refreshToken = localStorage.getItem(LOCAL_STORAGE.REFRESH_TOKEN);
    const userObj = localStorage.getItem(LOCAL_STORAGE.USER);
    if (userObj) {
      user = JSON.parse(userObj);
      // Maps user_type -> roles if missing
      // TODO: Remove in the future
      if (user && !user.roles) {
        const legacyRole = (user as any).user_type;
        user.roles = legacyRole ? [legacyRole] : [];
        localStorage.setItem(LOCAL_STORAGE.USER, JSON.stringify(user));
      }
    }
    const busObj = localStorage.getItem(LOCAL_STORAGE.BUSINESS);
    if (busObj) {
      business = JSON.parse(busObj);
    }
    dispatch({
      type: "STORE_TOKEN",
      user,
      business,
      jwtToken,
      refreshToken,
    });
  }, []);

  const authContext = useMemo(
    () => ({
      confirmationResult,
      signIn: (jwtToken: any, refreshToken: any, user: User, business: Business | null) => {
        // Sign In Method
        localStorage.setItem(LOCAL_STORAGE.USER, JSON.stringify(user));
        localStorage.setItem(LOCAL_STORAGE.TOKEN, jwtToken);
        localStorage.setItem(LOCAL_STORAGE.REFRESH_TOKEN, refreshToken);
        localStorage.setItem(LOCAL_STORAGE.BUSINESS, JSON.stringify(business));
        dispatch({
          type: "STORE_TOKEN",
          user,
          business,
          jwtToken,
          refreshToken,
        });
      },
      signOut: () => {
        // Sign Out Method
        localStorage.removeItem(LOCAL_STORAGE.USER);
        localStorage.removeItem(LOCAL_STORAGE.TOKEN);
        localStorage.removeItem(LOCAL_STORAGE.REFRESH_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE.BUSINESS);
        // The next login on this tab may be a different admin/business, so
        // remembered list positions must not carry over.
        clearRememberedTablePages();
        dispatch({ type: "DELETE_TOKEN" });
        queryClient.invalidateQueries();
      },
      recaptchaVerifier: async (phone_number: string) => {
        // Local development only: Firebase's own integration-testing switch.
        // It turns the reCAPTCHA into a no-op, and Firebase then accepts ONLY
        // the fictional test numbers configured in the console (their OTP is
        // fixed, no SMS is sent), so a real number still cannot get through.
        // CRA sets NODE_ENV to "production" for every build, and the hostname
        // check keeps a dev build served from anywhere else on the real widget.
        if (
          process.env.NODE_ENV === "development" &&
          ["localhost", "127.0.0.1"].includes(window.location.hostname)
        ) {
          auth.settings.appVerificationDisabledForTesting = true;
        }
        window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
          "expired-callback": () => {
            message.error(translate("messages.error.recaptcha_expired"));
          },
        });

        try {
          await window.recaptchaVerifier.render();
          // phone_number is already E.164 (see shared/utils/phone.ts), so this is
      // a pass-through. It used to glue on a hardcoded "+62".
      const confirmation = await signInWithPhoneNumber(auth, phone_number, window.recaptchaVerifier);
          setConfirmationResult(confirmation);
        } catch (err) {
          window.recaptchaVerifier.clear();
          throw err;
        }
      },
      confirmOtp: async (otp: string) => {
        try {
          if (!confirmationResult) throw Error("Recaptcha Error: No Confirmation Result");
          await confirmationResult.confirm(otp);
          setConfirmationResult(undefined);
        } catch (err) {
          throw err;
        } finally {
          window.recaptchaVerifier.clear();
        }
      },
      getPhoneProof: async () => {
        const current = auth.currentUser;
        if (!current) return null;
        return current.getIdToken(true);
      },
      clearPhoneProof: async () => {
        if (auth.currentUser) await firebaseSignOut(auth);
      },
      state,
    }),
    // eslint-disable-next-line
    [state, confirmationResult]
  );

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
};

export const AuthProviderWrapper = () => {
  // The router root: every layout renders under it.
  useCloseStaticModalsOnNavigation();
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within the AuthProvider");
  }
  return context;
};
